<template>
  <div class="privacy-policy-page">
    <v-container class="py-10">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="7">

          <div class="brand-header mb-8">
            <router-link to="/">
              <PassingOnLogo class="brand-logo" tone="primary" align="center" />
            </router-link>
          </div>

          <h1 class="policy-title mb-1">Privacy Policy</h1>
          <p class="policy-updated mb-8">Last updated: March 2026</p>

          <p class="policy-intro mb-8">
            This Privacy Policy explains how The Fourth Seagull Pty Ltd (ABN [insert]), registered in New South Wales,
            Australia, collects, uses, stores, and protects your personal information when you use
            <BrandName class="brand-inline" tone="primary" />
            (the "Service").
          </p>
          <p class="policy-body mb-4">
            We handle your personal data in accordance with the Australian Privacy Principles (APPs) under the
            Privacy Act 1988 (Cth).
          </p>
          <p class="policy-body mb-10">
            By using the Service, you consent to this Privacy Policy.
          </p>

          <section class="policy-section">
            <h2 class="section-heading">Information We Collect</h2>
            <p class="policy-body mb-4">We collect personal and technical information to provide and improve the Service:</p>
            <h3 class="subsection-heading">Information you provide directly:</h3>
            <ul class="policy-list mb-4">
              <li>Name, email address, and contact details when you register or contact us</li>
              <li>Billing and payment information (processed securely via Stripe — we never see or store card details)</li>
              <li>Memorial content you create (text, photos, videos, voice recordings, transcripts, stories, comments)</li>
              <li>Content you upload, share, or comment on</li>
              <li>Communication you send us (support requests, feedback, surveys)</li>
            </ul>
            <h3 class="subsection-heading">Information collected automatically:</h3>
            <ul class="policy-list mb-2">
              <li>Technical data such as browser type, device, IP address, and usage patterns (via cookies and analytics)</li>
              <li>Activity logs related to memory creation, sharing, and updates</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">How We Use Your Information</h2>
            <p class="policy-body mb-4">We use the information we collect to:</p>
            <ul class="policy-list mb-4">
              <li>Operate and maintain the platform and its features</li>
              <li>Enable user accounts, authentication, and payment processing</li>
              <li>Display and share memories as configured by the memory creator</li>
              <li>Transcribe uploaded voice recordings so an accessible text version can be displayed with the recording</li>
              <li>Communicate about your account, payments, or service changes</li>
              <li>Send product updates, service announcements, or optional marketing emails (you can unsubscribe anytime)</li>
              <li>Improve functionality, security, and performance</li>
              <li>Detect and prevent fraud, misuse, or security breaches</li>
              <li>Comply with legal and regulatory obligations</li>
            </ul>
            <p class="policy-body">We do not sell or rent your personal information to third parties.</p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Donations</h2>
            <p class="policy-body mb-4">When memory creators accept donations:</p>
            <ul class="policy-list mb-4">
              <li>Donations are processed through Stripe Connect. Funds flow directly from donor to memory creator's connected Stripe account</li>
              <li>We never hold, control, or disburse donated funds</li>
              <li>Stripe collects payment information directly from donors and memory creators who onboard to accept donations</li>
              <li>We charge a 1% platform fee, deducted automatically by Stripe</li>
              <li>Stripe's privacy policy applies to payment data they collect. We don't have access to full card details</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Cookies and Analytics</h2>
            <p class="policy-body mb-4">We use cookies and similar technologies to:</p>
            <ul class="policy-list mb-4">
              <li>Keep you logged in</li>
              <li>Measure site performance</li>
              <li>Understand how visitors use the Service</li>
              <li>Improve user experience</li>
            </ul>
            <p class="policy-body mb-4">
              You can disable cookies in your browser settings, but some parts of the Service may not work properly if you do.
            </p>
            <p class="policy-body">
              We may use third-party analytics providers (such as Google Analytics) to collect anonymous usage data.
              These providers don't have access to personally identifying information.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">How We Store and Protect Your Data</h2>
            <ul class="policy-list mb-2">
              <li>Data is stored securely on cloud infrastructure (AWS) with encryption and access controls</li>
              <li>We take reasonable technical and organisational measures to protect against unauthorised access, disclosure, alteration, or destruction of your data</li>
              <li>Staff and contractors are bound by confidentiality agreements</li>
              <li>Backup copies may be retained for up to 90 days for system integrity and disaster recovery</li>
              <li>While we take all reasonable care, no online platform can guarantee absolute security</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Data Retention and Deletion</h2>
            <ul class="policy-list mb-2">
              <li>Memories remain online for 12 months from activation, then switch to a monthly subscription</li>
              <li>If a subscription is cancelled or payment fails, you receive three reminders during a 90-day grace period</li>
              <li>If the subscription isn't reactivated, the memory and its data get permanently deleted after the grace period</li>
              <li>You may delete your account or memory at any time via your profile or by contacting us</li>
              <li>Deleted data may persist in encrypted backups for up to 90 days before permanent removal</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Sharing of Information</h2>
            <p class="policy-body mb-4">We only share personal information when necessary to operate the Service, including with:</p>
            <ul class="policy-list mb-4">
              <li>Payment processor (Stripe) for payment processing and Stripe Connect account management</li>
              <li>Hosting and infrastructure providers (AWS) for data storage and delivery</li>
              <li>Speech transcription providers (currently OpenAI) to convert voice recordings into text</li>
              <li>Email delivery services for account and notification emails</li>
              <li>Analytics providers for service improvement (non-identifiable data only)</li>
              <li>Legal authorities, if required by law or court order</li>
            </ul>
            <p class="policy-body mb-4">All third-party partners are required to comply with privacy and data protection standards.</p>
            <p class="policy-body">
              When you accept or make donations through the Service, Stripe collects and processes payment information
              directly. Stripe's privacy policy applies to data they collect.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">International Data Transfers</h2>
            <p class="policy-body mb-4">
              Our servers and third-party providers may operate in multiple locations, including outside Australia.
            </p>
            <p class="policy-body">
              Where personal data is transferred overseas, we take reasonable steps to ensure it's handled in
              accordance with this Privacy Policy and Australian privacy law.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Your Rights and Choices</h2>
            <p class="policy-body mb-4">You have the right to:</p>
            <ul class="policy-list mb-4">
              <li>Access and correct your personal information</li>
              <li>Request deletion of your account and associated data</li>
              <li>Opt out of marketing communications at any time</li>
              <li>Withdraw consent for data processing where applicable</li>
            </ul>
            <p class="policy-body">
              Requests can be made by contacting us at the email address below. We may require identity verification
              before processing such requests.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Children's Privacy</h2>
            <ul class="policy-list mb-2">
              <li>The Service is not intended for individuals under 18</li>
              <li>We don't knowingly collect personal information from minors</li>
              <li>If you believe a minor's data has been submitted, contact us to have it deleted</li>
            </ul>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Data Breach Notification</h2>
            <p class="policy-body mb-4">
              In the event of a data breach likely to result in serious harm, we will notify affected individuals
              and the Office of the Australian Information Commissioner (OAIC) as required under the Notifiable
              Data Breaches (NDB) scheme.
            </p>
            <p class="policy-body">
              We will take immediate steps to contain the breach, assess the risk, and implement remedial actions.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Changes to This Policy</h2>
            <p class="policy-body mb-4">
              We may update this Privacy Policy from time to time.
            </p>
            <p class="policy-body">
              Updates take effect when posted on the website. We'll notify users of significant changes by email
              or platform notice.
            </p>
          </section>

          <section class="policy-section">
            <h2 class="section-heading">Complaints</h2>
            <p class="policy-body mb-4">
              If you have a complaint about how we've handled your personal information, contact us using the
              details below. We'll investigate and respond within 30 days.
            </p>
            <p class="policy-body">
              If you're not satisfied with our response, you can lodge a complaint with the Office of the
              Australian Information Commissioner (OAIC) at
              <a href="https://www.oaic.gov.au" target="_blank" rel="noopener" class="policy-link">oaic.gov.au</a>.
            </p>
          </section>

          <section class="policy-section policy-contact">
            <h2 class="section-heading">Contact Us</h2>
            <p class="policy-body mb-1">For privacy-related questions, requests, or complaints:</p>
            <p class="policy-body mb-1"><strong>The Fourth Seagull Pty Ltd</strong></p>
            <p class="policy-body mb-1">Email: [insert contact email]</p>
            <p class="policy-body">Registered in New South Wales, Australia</p>
          </section>

          <div class="back-link mt-10">
            <router-link to="/" class="policy-link">← Back to home</router-link>
          </div>

        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import PassingOnLogo from '../components/PassingOnLogo.vue'
import BrandName from '../components/BrandName.vue'
</script>

<style scoped lang="scss">
@use '../styles/variables' as *;

.privacy-policy-page {
  min-height: 100vh;
  background-color: $white;
}

.brand-header {
  text-align: center;
}

.brand-logo {
  height: 48px;
}

.policy-title {
  font-family: 'Alice', serif;
  font-size: 2rem;
  color: $text-primary;
}

.policy-updated {
  font-size: 0.875rem;
  color: $text-muted;
}

.policy-intro {
  font-size: 1rem;
  line-height: 1.7;
  color: $text-primary;
}

.policy-body {
  font-size: 1rem;
  line-height: 1.7;
  color: $text-secondary;
  margin: 0;
}

.policy-section {
  margin-bottom: 2.5rem;
}

.section-heading {
  font-family: 'Alice', serif;
  font-size: 1.25rem;
  color: $text-primary;
  margin-bottom: 0.75rem;
  padding-bottom: 0.4rem;
  border-bottom: $border-width solid $gray-150;
}

.subsection-heading {
  font-size: 0.95rem;
  font-weight: 600;
  color: $text-primary;
  margin-bottom: 0.5rem;
}

.policy-list {
  padding-left: 1.25rem;
  color: $text-secondary;
  line-height: 1.8;

  li {
    margin-bottom: 0.35rem;
  }
}

.policy-contact {
  background-color: $surface;
  border: $border-width solid $gray-150;
  border-radius: $border-radius;
  padding: 1.5rem;
}

.policy-link {
  color: $primary;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.back-link {
  border-top: $border-width solid $gray-150;
  padding-top: 1.5rem;
}

.brand-inline {
  font-size: 1rem;
  letter-spacing: normal;
}
</style>
